/**
 * JIRA Service API client hook (PHOTON-76).
 *
 * Mirrors useFreshdeskApiClient: JSON endpoints for thread CRUD + /ping, and
 * a hand-parsed SSE streaming method for POST /api/jira/ask (EventSource only
 * supports GET; ours is POST).
 *
 * SSE event vocabulary is identical to the freshdesk service:
 *   delta / round_end / tool_call / tool_result / usage / error / done
 */
import { useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";

import { useDevBypass } from "../utils/devBypass";
import { useTokenManager } from "./useTokenManager";
import { getConfig } from "../config";

export interface JiraUsage {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  rounds?: number;
}

/** A persisted chat message (text turns only — no tool blocks). */
export interface JiraChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  input_tokens?: number | null;
  output_tokens?: number | null;
}

/** Full thread with its messages (GET /threads/{id}). */
export interface JiraThread {
  id: string;
  user_id: string;
  title: string;
  messages: JiraChatMessage[];
  created_at: string;
  updated_at: string;
  message_count: number;
}

/** Lightweight row for the sidebar list (GET /threads). */
export interface JiraThreadSummary {
  id: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  preview: string | null;
}

/** Agentic event: the model decided to invoke a tool. */
export interface JiraToolCall {
  round: number;
  tool_use_id: string;
  name: string;
  input: Record<string, unknown>;
}

/** Agentic event: the tool returned. `summary` is shaped per tool name;
 *  `summary.write` marks tools that mutated JIRA. */
export interface JiraToolResult {
  tool_use_id: string;
  summary: {
    ok: boolean;
    write?: boolean;
    error?: string;
    detail?: string;
    issue_count?: number;
    is_last_page?: boolean;
    approximate_count?: number;
    issue_key?: string;
    summary_text?: string;
    comment_count?: number;
    project_count?: number;
    issue_type_count?: number;
    user_count?: number;
    new_status?: string;
    updated_fields?: string[];
  };
}

/** Emitted at the end of every agent round. See `onRoundEnd`. */
export interface JiraRoundEnd {
  round: number;
  /**
   * True when the model called tools in this round, which means the prose it
   * produced was a PLAN, not a result — the tools had not run when it was
   * written. PHOTON-107: treating that text as the answer is how "Closed
   * SV-112" reached a user for a ticket that was never closed.
   */
  had_tool_calls: boolean;
  /**
   * True when a WRITE tool ran in this round, which means the prose was
   * written before that write executed. This, not `had_tool_calls`, decides
   * whether the text may stand as the answer: commentary alongside read calls
   * is real information, but "Closed SV-112" written beside the call that
   * closes it is a prediction.
   */
  had_write_calls: boolean;
  text: string;
}

export interface StreamAskHandlers {
  onDelta?: (text: string, round?: number) => void;
  /**
   * Fires once per round. When `had_tool_calls` is true the caller MUST NOT
   * keep that round's text as the assistant's answer.
   */
  onRoundEnd?: (info: JiraRoundEnd) => void;
  onToolCall?: (call: JiraToolCall) => void;
  onToolResult?: (result: JiraToolResult) => void;
  onUsage?: (u: JiraUsage) => void;
  onError?: (code: string, message: string) => void;
  onDone?: () => void;
}

export interface JiraApiClient {
  /** Diagnostic — proves the service can reach JIRA Cloud. */
  ping: () => Promise<unknown>;
  /**
   * Stream a /api/jira/ask response. Returns a cancel function that aborts
   * the underlying fetch when the caller wants to walk away.
   */
  streamAsk: (
    body: { question: string; thread_id?: string },
    handlers: StreamAskHandlers,
  ) => () => void;
  // Thread persistence
  createThread: (initialMessage?: string) => Promise<JiraThread>;
  listThreads: () => Promise<JiraThreadSummary[]>;
  getThread: (id: string) => Promise<JiraThread>;
  renameThread: (id: string, title: string) => Promise<JiraThread>;
  deleteThread: (id: string) => Promise<void>;
  getBaseUrl: () => string;
  isConfigured: () => boolean;
}

/** Parse one fully buffered SSE event ("event: foo\ndata: {...}") into (event, payload). */
function parseSseEvent(raw: string): { event: string; data: unknown } | null {
  const lines = raw.split(/\r?\n/);
  let event = "message";
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  const joined = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(joined) };
  } catch {
    return { event, data: joined };
  }
}

export function useJiraApiClient(): JiraApiClient {
  const { instance } = useMsal();
  const tokenManager = useTokenManager();
  const inflight = useRef<AbortController | null>(null);

  const getAuthHeaders = useCallback(async (forceRefresh = false): Promise<HeadersInit> => {
    const token = await tokenManager.getToken(forceRefresh);
    const { userId, email, roles } = tokenManager.getUserInfo();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-User-Id": userId,
      "X-User-Email": email,
      "X-User-Roles": roles.join(","),
    };
  }, [tokenManager]);

  const ping = useCallback(async () => {
    const headers = await getAuthHeaders();
    const base = getConfig().jiraServiceUrl || "";
    const r = await fetch(`${base}/api/jira/ping`, { headers });
    if (r.status === 401 && !useDevBypass) {
      try { await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] }); } catch {}
      throw new Error("Session expired. Redirecting to login...");
    }
    if (!r.ok) {
      const text = await r.text().catch(() => r.statusText);
      throw new Error(`HTTP ${r.status}: ${text}`);
    }
    return r.json();
  }, [getAuthHeaders, instance]);

  /** Authenticated JSON request against the jira service. */
  const authedJson = useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      const headers = await getAuthHeaders();
      const base = getConfig().jiraServiceUrl || "";
      if (!base) throw new Error("jiraServiceUrl is not configured");
      const r = await fetch(`${base}${path}`, {
        ...init,
        headers: { ...headers, ...(init?.headers || {}) },
      });
      if (r.status === 401 && !useDevBypass) {
        try { await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] }); } catch {}
        throw new Error("Session expired. Redirecting to login...");
      }
      if (!r.ok) {
        const text = await r.text().catch(() => r.statusText);
        throw new Error(`HTTP ${r.status}: ${text}`);
      }
      if (r.status === 204) return undefined as T;
      return r.json();
    },
    [getAuthHeaders, instance],
  );

  const createThread = useCallback(
    (initialMessage?: string) =>
      authedJson<JiraThread>("/api/jira/threads", {
        method: "POST",
        body: JSON.stringify({ initial_message: initialMessage ?? null }),
      }),
    [authedJson],
  );

  const listThreads = useCallback(
    () => authedJson<JiraThreadSummary[]>("/api/jira/threads"),
    [authedJson],
  );

  const getThread = useCallback(
    (id: string) => authedJson<JiraThread>(`/api/jira/threads/${id}`),
    [authedJson],
  );

  const renameThread = useCallback(
    (id: string, title: string) =>
      authedJson<JiraThread>(`/api/jira/threads/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      }),
    [authedJson],
  );

  const deleteThread = useCallback(
    async (id: string) => {
      await authedJson<{ status: string }>(`/api/jira/threads/${id}`, {
        method: "DELETE",
      });
    },
    [authedJson],
  );

  const streamAsk = useCallback(
    (
      body: { question: string; thread_id?: string },
      handlers: StreamAskHandlers,
    ): (() => void) => {
      // Cancel any prior stream from this hook instance.
      if (inflight.current) {
        inflight.current.abort();
      }
      const controller = new AbortController();
      inflight.current = controller;

      (async () => {
        try {
          const headers = await getAuthHeaders();
          const base = getConfig().jiraServiceUrl || "";
          if (!base) {
            handlers.onError?.("NO_BASE_URL", "jiraServiceUrl is not configured");
            handlers.onDone?.();
            return;
          }
          const resp = await fetch(`${base}/api/jira/ask`, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
            signal: controller.signal,
          });
          if (resp.status === 401 && !useDevBypass) {
            try { await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] }); } catch {}
            handlers.onError?.("UNAUTHORIZED", "Session expired");
            handlers.onDone?.();
            return;
          }
          if (!resp.ok || !resp.body) {
            const text = await resp.text().catch(() => resp.statusText);
            handlers.onError?.(`HTTP_${resp.status}`, text || `HTTP ${resp.status}`);
            handlers.onDone?.();
            return;
          }

          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buf = "";
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            // SSE events are delimited by a blank line — "\n\n" or "\r\n\r\n".
            let idx;
            while ((idx = buf.search(/\r?\n\r?\n/)) !== -1) {
              const rawEvent = buf.slice(0, idx);
              const match = buf.slice(idx).match(/^\r?\n\r?\n/);
              buf = buf.slice(idx + (match ? match[0].length : 2));
              const parsed = parseSseEvent(rawEvent);
              if (!parsed) continue;
              const { event, data } = parsed;
              if (event === "delta") {
                const d = data as { text: string; round?: number };
                handlers.onDelta?.(d.text ?? "", d.round);
              } else if (event === "round_end") {
                handlers.onRoundEnd?.(data as JiraRoundEnd);
              } else if (event === "tool_call") {
                handlers.onToolCall?.(data as JiraToolCall);
              } else if (event === "tool_result") {
                handlers.onToolResult?.(data as JiraToolResult);
              } else if (event === "usage") {
                handlers.onUsage?.(data as JiraUsage);
              } else if (event === "error") {
                const d = data as { code: string; message: string };
                handlers.onError?.(d.code ?? "UNKNOWN", d.message ?? "");
              } else if (event === "done") {
                handlers.onDone?.();
                return;
              }
            }
          }
          handlers.onDone?.();
        } catch (err: unknown) {
          if ((err as { name?: string }).name === "AbortError") return;
          const msg = err instanceof Error ? err.message : String(err);
          handlers.onError?.("STREAM_FAILED", msg);
          handlers.onDone?.();
        } finally {
          if (inflight.current === controller) {
            inflight.current = null;
          }
        }
      })();

      return () => controller.abort();
    },
    [getAuthHeaders, instance],
  );

  // Memoize so consumers get a stable reference across renders — an unstable
  // client object in an effect/callback dependency array causes refetch loops.
  return useMemo(
    () => ({
      ping,
      streamAsk,
      createThread,
      listThreads,
      getThread,
      renameThread,
      deleteThread,
      getBaseUrl: () => getConfig().jiraServiceUrl || "",
      isConfigured: () => !!getConfig().jiraServiceUrl,
    }),
    [ping, streamAsk, createThread, listThreads, getThread, renameThread, deleteThread],
  );
}

export default useJiraApiClient;
